// Whether a set of vehicles can carry a booking's passengers and luggage.
//
// A copy of routes/utils/vehicleCapacity.js, which the backend applies when an
// edited booking is submitted -- keep the two in step. Both follow the rule the
// vehicle search uses, so a vehicle the search offers is never rejected
// afterwards.
//
// How capacity is measured:
// - Luggage is counted in "cabin-size units": a cabin bag is 1 unit, a
//   check-in bag takes the room of 2 cabin bags.
// - A vehicle's luggage room depends on how many passengers ride in it. Each
//   vehicle type stores this as `calculationRules`: [{ pax, cabinLuggage }],
//   e.g. HiAce Luxury Cabin carries 24 units with 4 passengers but 12 with 9.
//
// `maxluggages` is only the "up to N suitcases" figure shown to customers. It
// must not be used for this check: adding check-in and cabin bags together
// and comparing with it rejected bookings the vehicle can really carry.

const CHECK_IN_BAG_UNITS = 2;
const CABIN_BAG_UNITS = 1;

/**
 * Total luggage in cabin-size units.
 *
 * @param {{checkinSize?: number, cabinSize?: number}} luggage
 * @returns {number}
 */
export const getLuggageUnits = (luggage) =>
  Number(luggage?.checkinSize || 0) * CHECK_IN_BAG_UNITS +
  Number(luggage?.cabinSize || 0) * CABIN_BAG_UNITS;

/**
 * Luggage units one vehicle holds while carrying `passengerCount` people, or
 * null when it cannot carry that many. A vehicle sent with no passengers (only
 * luggage) gets the room of its 1-passenger rule -- the lightest load it is
 * configured for.
 */
const getVehicleLuggageUnitsForPassengers = (vehicleType, passengerCount) => {
  const ridersForRule = Math.max(passengerCount, 1);
  const matchingRule = (vehicleType?.calculationRules || []).find(
    (rule) => Number(rule.pax) === ridersForRule
  );
  return matchingRule ? Number(matchingRule.cabinLuggage || 0) : null;
};

/**
 * The most luggage units the vehicles hold together when all passengers are
 * seated, choosing the best split of passengers between vehicles. Returns null
 * when the passengers cannot be seated at all.
 *
 * Uses a small table: `bestUnitsBySeatedCount[n]` is the most luggage room
 * found so far with exactly n passengers seated in the vehicles handled so
 * far. Each vehicle is added in turn, trying every passenger count it can
 * take. Bookings have a handful of vehicles and passengers, so this is cheap.
 */
const getBestLuggageUnits = (vehicleTypes, totalPassengers) => {
  let bestUnitsBySeatedCount = [0]; // no vehicles yet: 0 seated, 0 room

  for (const vehicleType of vehicleTypes) {
    const maxRidersInVehicle = Number(vehicleType?.maxpassenger || 0);
    const nextBestUnits = [];

    bestUnitsBySeatedCount.forEach((unitsSoFar, seatedSoFar) => {
      if (unitsSoFar === undefined) return;

      for (let riders = 0; riders <= maxRidersInVehicle; riders++) {
        const seated = seatedSoFar + riders;
        if (seated > totalPassengers) break;

        const vehicleUnits = getVehicleLuggageUnitsForPassengers(vehicleType, riders);
        if (vehicleUnits === null) continue;

        const combinedUnits = unitsSoFar + vehicleUnits;
        if (nextBestUnits[seated] === undefined || combinedUnits > nextBestUnits[seated]) {
          nextBestUnits[seated] = combinedUnits;
        }
      }
    });

    bestUnitsBySeatedCount = nextBestUnits;
  }

  const bestUnits = bestUnitsBySeatedCount[totalPassengers];
  return bestUnits === undefined ? null : bestUnits;
};

/**
 * Whether the vehicles can carry the passengers and the luggage together.
 *
 * @param {Array<{maxpassenger: number, calculationRules: Array}>} vehicleTypes
 * @param {number} totalPassengers
 * @param {{checkinSize?: number, cabinSize?: number}} luggage
 * @returns {{canCarry: boolean, requiredLuggageUnits: number, availableLuggageUnits: number|null}}
 */
export const checkVehicleCapacity = (vehicleTypes, totalPassengers, luggage) => {
  const requiredLuggageUnits = getLuggageUnits(luggage);

  if (!Array.isArray(vehicleTypes) || vehicleTypes.length === 0) {
    return { canCarry: false, requiredLuggageUnits, availableLuggageUnits: null };
  }

  const availableLuggageUnits = getBestLuggageUnits(vehicleTypes, Number(totalPassengers || 0));
  const canCarry = availableLuggageUnits !== null && requiredLuggageUnits <= availableLuggageUnits;

  return { canCarry, requiredLuggageUnits, availableLuggageUnits };
};
